<template>
  <div class="verify-income m-auto">
    <StepHeader
      :title="title"
      :theme="theme"
    />

    <StepContent
      :theme="theme"
      width="max-w-600"
    >
      <DocumentUpload
        :rules="rules"
        :upload-buttons="uploadButtons"
        :docs-stand-alone="docsStandAlone"
        :theme="theme"
      >
        <template #step-summary>
          <p class="text-start">
            We accept many types of income. Please upload any of the following:
          </p>
        </template>
      </DocumentUpload>
    </StepContent>
    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="submitting"
        @click="checkStatusBeforeSubmission()"
      >
        Continue
      </AppButton>

      <StepSkip
        verification-step="paystubs"
        :theme="theme"
        @skip="handleSkip"
      />
    </StepFooter>

    <AppPopupConfirmSubmit
      v-if="showPopup"
      :theme="theme"
      @continue="checkStatusBeforeSubmission(true)"
      @try-again="removePopUp()"
    />
  </div>
</template>

<script>
import StepHeader from '@/components/common/step-layout/StepHeader'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepSkip from '@/components/common/step-layout/StepSkip'
import AppButton from '@/components/common/buttons/AppButton'
import DocumentUpload from '@/components/common/document-upload/DocumentUpload'
import submitLoans from '@/mixins/submit/submit-loans'
import navigation from '@/mixins/navigation'
import AppPopupConfirmSubmit from '@/components/common/popups/AppPopupConfirmSubmit'

export default {
  name: 'VerifyIncome',

  // Components
  components: {
    StepHeader,
    StepContent,
    DocumentUpload,
    AppPopupConfirmSubmit,
    StepFooter,
    StepSkip,
    AppButton
  },

  // Mixins
  mixins: [
    navigation,
    submitLoans
  ],

  // Props
  props: {
    docsStandAlone: {
      type: Boolean,
      default: false
    },

    theme: {
      type: String,
      default: 'green'
    }
  },

  // Data
  data () {
    return {
      showPopup: false,
      submitting: false
    }
  },

  // Computed
  computed: {
    title () {
      return 'Verify Your Income'
    },

    rules () {
      return [
        'Full-Time or Part Time: Last 2 pay stubs.',
        'Self Employed: Last 2 notice of assessments.',
        'Child Tax: 90 days of bank statements.',
        'Disability: Disability statement showing long term and the full amount.',
        'EI: Record of employment.'
      ]
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    dataLayerObject () {
      const eventName = this.returningCustomer ? 'spring_ploan_returning_customer_income_step_completed' : 'spring_ploan_income_step_completed'
      return {
        event: eventName
      }
    },

    dataLayerSkip () {
      return {
        event: 'spring_ploan_verifyincome_skip'
      }
    },

    dataLayerUpload1 () {
      const eventName = this.returningCustomer ? 'spring_ploan_returning_customer_income_document_upload1' : 'spring_ploan_income_document_upload1'
      return {
        event: eventName
      }
    },

    dataLayerUpload2 () {
      const eventName = this.returningCustomer ? 'spring_ploan_returning_customer_income_document_upload2' : 'spring_ploan_income_document_upload2'
      return {
        event: eventName
      }
    },

    incomeFile1Error () {
      return this.$store.state.validations.incomeFile1Error
    },

    incomeFile2Error () {
      return this.$store.state.validations.incomeFile2Error
    },

    uploadButtons () {
      return [
        {
          documentType: 'Proof of Income',
          title: 'Upload Proof of Income 1',
          value: this.file1,
          vuexAction: 'setProofOfIncome',
          vuexActionFile: 'setProofOfIncomeFile1',
          required: !this.proofOfIncome,
          dataLayerEvent: this.dataLayerUpload1,
          error: this.incomeFile1Error
        },
        {
          documentType: 'Proof of Income',
          noFileChosenText: '(If required)',
          title: 'Upload Proof of Income 2',
          value: this.file2,
          vuexAction: 'setProofOfIncome',
          vuexActionFile: 'setProofOfIncomeFile2',
          required: false,
          dataLayerEvent: this.dataLayerUpload2,
          error: this.incomeFile2Error
        }
      ]
    },

    file1 () {
      return this.$store.state.documents.proofOfIncomeFile1
    },

    file2 () {
      return this.$store.state.documents.proofOfIncomeFile2
    },

    proofOfIncome () {
      return this.$store.state.documents.proofOfIncome
    }
  },

  // Watch
  watch: {
    proofOfIncome () {
      // for some reason it eneds this watcher to update the button

    },
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.checkStatusBeforeSubmission()
      this.$store.dispatch('setTriggerContinue', false)
    },

    $_navigation_triggerSkip: function (val) {
      if (val) this.handleSkip()
      this.$store.dispatch('setTriggerSkipStep', false)
    }
  },

  // Methods
  methods: {
    handleSkip () {
      this.$store.dispatch('setSkippedStep', true)
      this.$_navigation_handleNext(this.dataLayerSkip, true)
    },

    async checkStatusBeforeSubmission (continueSubmission = false) {
      let isValid = await this.$_navigation_validateStep()
      if (isValid && this.proofOfIncome) {
        this.showPopup = false
        this.$_loans_submitLeadIfReady(false, false, this.dataLayerObject)
      } else if (continueSubmission) {
        this.showPopup = false
        this.$_loans_submitLeadIfReady(false, true, this.dataLayerObject)
      } else if (this.proofOfIncome) {
        this.showPopup = true
      }
    },

    removePopUp () {
      this.showPopup = false
    }
  }
}
</script>
